/*
 * Handles all the search requests for topics.
 * Defines a search results group, which is an extension of a topic group.  We use this model to allow
 * search results to mimic a topic group so we can reuse the topicsList module to show them.
 */

define([
  "backbone",
  "jquery",
  "configuration/properties",
  "messaging/messenger",
  "modules/topicsSplit/topics/topicsModel",
], function (Backbone, $, Properties, Messenger, TopicsModel) {
  var TopicSearchModel = {};
  const TopicTypes = {
    NON_ARCHIVED: "NON_ARCHIVED",
    ARCHIVED: "ARCHIVED",
    ALL: "ALL",
  };

  //Topic search results model extends the topic group model so it can be used in the topicsList module
  TopicSearchModel.topicSearchResults = TopicsModel.topicGroup.extend({
    defaults: {
      groupId: -1,
      name: "",
      description: "",
      topicType: TopicTypes.NON_ARCHIVED,
    },
    topics: function () {
      if (!this.topicsCol) {
        this.topicsCol = new TopicsModel.topics();
        this.topicsCol.url = this.get("searchUrl"); //use the search url provided during model creation
        this.topicsCol.fetch();
      }
      return this.topicsCol;
    },

    // Override archivedTopics to call topics (since we're using the same collection)
    archivedTopics: function () {
      return this.topics();
    },
    updateRestoredTopics: function (restoredTopics) {
      restoredTopics.forEach((topic) => {
        this.topicsCol.remove(topic);
      });
    },
    updateArchivedTopics: function (archivedTopics) {
      archivedTopics.forEach((topic) => {
        this.topicsCol.remove(topic);
      });
    },
  });

  //Topic search results that have a maximum limit
  TopicSearchModel.limitedTopicSearchResults = TopicsModel.topicGroup.extend({
    defaults: {
      groupId: -2, //special group ID for limited searches
      name: "",
      description: "",
      exceededLimit: false,
      limit: 0,
      topicType: TopicTypes.NON_ARCHIVED,
    },
    //gets the topics
    topics: function () {
      if (!this.topicsCol) {
        //the search url does not return a list of topics, but an object.  So fetch the results in a temporary model
        //and then get them when synced
        var searchResults = new Backbone.Model();
        searchResults.url = this.get("searchUrl"); //use the search url provided during model creation
        this.topicsCol = new TopicsModel.topics();
        searchResults.fetch();
        //trigger the request event to make the collection appear to be requesting data from the server
        this.topicsCol.trigger("request");
        searchResults.onSynced(function () {
          this.topicsCol.add(searchResults.get("returnedMatches"), { silent: true });
          if (searchResults.get("exceededLimit")) {
            this.set("limit", searchResults.get("returnedMatches").length);
            this.set("exceededLimit", true);
          }
          //make the collection appear to have synced
          this.topicsCol.trigger("fetched");
          this.topicsCol.trigger("sync");
        }, this);
      }
      return this.topicsCol;
    },
  });

  //Topic replace model extends the topic group model.
  TopicSearchModel.topicReplaceResults = TopicsModel.topicGroup.extend({
    defaults: {
      groupId: -1,
      name: "",
      description: "",
      topicType: TopicTypes.NON_ARCHIVED,
    },
    findAndReplace: function (findText, replaceText, touchpoint, language, matchCase, wholeWord) {
      //create a find and replace request model
      //trigger the find and replace PUT call from the request model
      //when the request completes, parse the results into a topics collection (which we passed to the request object)
      var request = new TopicSearchModel.topicReplaceRequest();
      this.topicsCol = new TopicsModel.topics();
      request.replaceRequest(
        findText,
        replaceText,
        touchpoint,
        language,
        matchCase,
        wholeWord,
        this.topicsCol,
      );
    },
    //gets the topics that were returned from the find and replace (if any)
    topics: function () {
      if (!this.topicsCol) {
        this.topicsCol = new TopicsModel.topics();
      }
      return this.topicsCol;
    },
  });

  //Topic find and replace request model that actually sends the replace request
  TopicSearchModel.topicReplaceRequest = Backbone.Model.extend({
    idAttribute: "findText", //give it an id attribute to force PUT request (instead of POST)
    replaceRequest: function (
      findText,
      replaceText,
      touchpoint,
      language,
      matchCase,
      wholeWord,
      topicsCollection,
    ) {
      //set the model attributes
      this.set("findText", findText);
      this.set("replaceText", replaceText);
      this.set("touchPointId", touchpoint);
      this.set("languageId", language);
      this.set("matchCase", matchCase);
      this.set("matchWholeWord", wholeWord);
      //make the request
      topicsCollection.trigger("request"); //trigger a "request" event to simulate the collection actually requesting these models, as opposed to topicReplaceRequest...
      this.save(null, {
        success: function (model, response) {
          //insert the models from the response into the passed topic collection and trigger a fetch and sync on the collection as if the collection fetched the data
          topicsCollection.set(response, { silent: true });
          topicsCollection.trigger("fetched");
          topicsCollection.trigger("sync");
        },
      });
    },
    url: function () {
      var kbId = Messenger.reqres.request("active:kb");
      return Properties.rootURL + "/" + kbId + "/intents/responses/findandreplace";
    },
  });

  Messenger.reqres.setHandler(
    "search:topics:by:name",
    function (name, topicType) {
      if (!topicType) {
        topicType = TopicTypes.NON_ARCHIVED;
      }
      var kbId = Messenger.reqres.request("active:kb");
      var searchUrl =
        `${Properties.rootURL}/${kbId}/` +
        `intents/groups/0/` +
        `topicType/${topicType}/topic_name/search` +
        `?topicName=${encodeURIComponent(name)}`;
      //create a topicSearchResults model and assign it a search url
      var searchTitle = $.t("topics.search.searchResults", { criteria: name.escapeHtml() });
      var returnObj = new TopicSearchModel.topicSearchResults({
        searchUrl: searchUrl,
        name: searchTitle,
      });
      return returnObj;
    },
    this,
  );

  Messenger.reqres.setHandler(
    "search:topics:by:example",
    function (example, locales, topicType) {
      if (!topicType) topicType = TopicTypes.NON_ARCHIVED;
      var localeIds = locales && locales.length > 0 ? locales.join("_") : "";
      var kbId = Messenger.reqres.request("active:kb");
      var searchUrl =
        `${Properties.rootURL}/${kbId}/` +
        `intents/groups/0/` +
        `topicType/${topicType}/example_text/search` +
        `?exampleText=${encodeURIComponent(example)}` +
        `&localeIds=${encodeURIComponent(localeIds)}`;
      //create a topicSearchResults model and assign it a search url
      var searchTitle = $.t("topics.search.searchResults", { criteria: example.escapeHtml() });
      var returnObj = new TopicSearchModel.topicSearchResults({
        searchUrl: searchUrl,
        name: searchTitle,
      });
      return returnObj;
    },
    this,
  );

  Messenger.reqres.setHandler(
    "search:topics:by:label",
    function (example, locale, topicType) {
      if (!topicType) topicType = TopicTypes.NON_ARCHIVED;
      var kbId = Messenger.reqres.request("active:kb");
      var searchUrl =
        `${Properties.rootURL}/${kbId}/` +
        `intents/groups/0/` +
        `locales/${locale}/` +
        `topicType/${topicType}/search` +
        `?labelText=${encodeURIComponent(example)}`;
      //create a topicSearchResults model and assign it a search url
      var searchTitle = $.t("topics.search.searchResults", { criteria: example.escapeHtml() });
      var returnObj = new TopicSearchModel.topicSearchResults({
        searchUrl: searchUrl,
        name: searchTitle,
      });
      return returnObj;
    },
    this,
  );

  Messenger.reqres.setHandler(
    "search:topics:by:response",
    function (response, touchpoint, language, matchCase, wholeWord, searchHtml, topicType) {
      if (!topicType) topicType = TopicTypes.NON_ARCHIVED;
      var escapedResponse = response.escapeHtml();
      var responseText;
      //use the escaped response text when searching just text.  Use the raw text when searching the HTML
      responseText = searchHtml ? response : escapedResponse;

      var kbId = Messenger.reqres.request("active:kb");
      var searchUrl =
        `${Properties.rootURL}/${kbId}/` +
        `intents/groups/0/` +
        `touchpoints/${touchpoint}/` +
        `languages/${language}/` +
        `matchcase/${matchCase}/` +
        `wholeword/${wholeWord}/` +
        `searchhtml/${searchHtml}/` +
        `limited/topicType/${topicType}/search` +
        `?responseText=${encodeURIComponent(responseText)}`;
      //create a topicSearchResults model and assign it a search url
      var searchTitle = $.t("topics.search.searchResults", { criteria: escapedResponse });
      var returnObj = new TopicSearchModel.limitedTopicSearchResults({
        searchUrl: searchUrl,
        name: searchTitle,
      });
      return returnObj;
    },
    this,
  );

  Messenger.reqres.setHandler(
    "search:topics:by:associated:content",
    function (mappingName, topicType) {
      if (!topicType) topicType = TopicTypes.NON_ARCHIVED;
      var kbId = Messenger.reqres.request("active:kb");
      var searchUrl =
        `${Properties.rootURL}/${kbId}/` +
        `intents/groups/0/` +
        `topicType/${topicType}/mappings/search` +
        `?mappingName=${encodeURIComponent(mappingName)}`;

      //create a topicSearchResults model and assign it a search url
      var searchTitle = $.t("topics.search.searchResults", { criteria: mappingName.escapeHtml() });
      var returnObj = new TopicSearchModel.topicSearchResults({
        searchUrl: searchUrl,
        name: searchTitle,
      });
      return returnObj;
    },
    this,
  );

  Messenger.reqres.setHandler(
    "search:topics:by:sub:group",
    function (subGroupName, topicType) {
      if (!topicType) topicType = TopicTypes.NON_ARCHIVED;
      var kbId = Messenger.reqres.request("active:kb");
      var searchUrl =
        `${Properties.rootURL}/${kbId}/` +
        `intents/groups/0/` +
        `topicType/${topicType}/substitutions/search` +
        `?subGroupName=${encodeURIComponent(subGroupName)}`;
      //create a topicSearchResults model and assign it a search url
      var searchTitle = $.t("topics.search.searchResults", { criteria: subGroupName.escapeHtml() });
      var returnObj = new TopicSearchModel.topicSearchResults({
        searchUrl: searchUrl,
        name: searchTitle,
      });
      return returnObj;
    },
    this,
  );

  Messenger.reqres.setHandler(
    "search:topics:by:decision",
    function (decisionChoice, topicType) {
      if (!topicType) topicType = TopicTypes.NON_ARCHIVED;
      var kbId = Messenger.reqres.request("active:kb");
      var searchUrl =
        `${Properties.rootURL}/${kbId}/` +
        `intents/groups/0/` +
        `topicType/${topicType}/decision_choices/search` +
        `?decisionChoice=${encodeURIComponent(decisionChoice)}`;

      //create a topicSearchResults model and assign it a search url
      var searchTitle = $.t("topics.search.searchResults", {
        criteria: decisionChoice.escapeHtml(),
      });
      var returnObj = new TopicSearchModel.topicSearchResults({
        searchUrl: searchUrl,
        name: searchTitle,
      });
      return returnObj;
    },
    this,
  );

  Messenger.reqres.setHandler(
    "search:topics:by:status",
    function (statusId, statusName, topicType) {
      if (!topicType) topicType = TopicTypes.NON_ARCHIVED;
      var kbId = Messenger.reqres.request("active:kb");
      var searchUrl =
        Properties.rootURL +
        "/" +
        kbId +
        "/intents/groups/0/status/" +
        statusId +
        "/topicType/" +
        topicType;
      //create a topicSearchResults model and assign it a search url
      var searchTitle = $.t("topics.search.searchResults", { criteria: statusName });
      var returnObj = new TopicSearchModel.topicSearchResults({
        searchUrl: searchUrl,
        name: searchTitle,
      });
      return returnObj;
    },
    this,
  );

  Messenger.reqres.setHandler(
    "search:topics:by:task:assignee:and:status",
    function (userLogin, taskStatus) {
      var kbId = Messenger.reqres.request("active:kb");
      var searchUrl =
        `${Properties.rootURL}/${kbId}/` +
        `intents/groups/0/tasks/` +
        `assignee_login/${encodeURIComponent(userLogin)}/` +
        `status/${taskStatus}`;
      //create a topicSearchResults model and assign it a search url
      var searchTitle = $.t("topics.search.searchResults", {
        criteria: userLogin,
      }); //modify this if we ever use this in a real search
      var returnObj = new TopicSearchModel.topicSearchResults({
        searchUrl: searchUrl,
        name: searchTitle,
      });
      return returnObj;
    },
    this,
  );

  Messenger.reqres.setHandler(
    "search:topics:by:topic:review",
    function (timestamp, assignee, creator, type) {
      type = Number(type);
      var kbId = Messenger.reqres.request("active:kb");
      var timeStampSpecified = true;
      var assigneeSpecified = true;
      var creatorSpecified = true;
      if (timestamp === "") {
        timeStampSpecified = false;
        timestamp = 253370764800000; //Is this too big for JavaScript???
      }
      if (assignee === "") {
        //handle the case of searching for everyone with a reserved word
        assigneeSpecified = false;
        assignee = "_all_";
      }
      if (creator === "") {
        //handle searching across all creators
        creator = "_all_";
        creatorSpecified = false;
      }
      var searchUrl =
        `${Properties.rootURL}/${kbId}/` +
        `tasks/type/${type}/timestamp/${timestamp}/search` +
        `?assignee=${encodeURIComponent(assignee)}` +
        `&creator=${encodeURIComponent(creator)}`;
      var searchTitle = "";
      //Pick one of seven search titles
      if (!assigneeSpecified && !creatorSpecified && !timeStampSpecified && type === -1) {
        searchTitle = $.t("topics.search.allTasks");
      } else if (assigneeSpecified && !creatorSpecified && !timeStampSpecified && type === -1) {
        searchTitle = $.t("topics.search.allTasksForPerson", { person: assignee });
      } else if (!assigneeSpecified && creatorSpecified && !timeStampSpecified && type === -1) {
        searchTitle = $.t("topics.search.tasksCreatedByPerson", { person: creator });
      } else if (!assigneeSpecified && !creatorSpecified && timeStampSpecified && type === -1) {
        searchTitle = $.t("topics.search.tasksDueBy", {
          date: Messenger.reqres.request("get:formatted:date", timestamp),
        });
      } else if (!assigneeSpecified && !creatorSpecified && !timeStampSpecified && type === 0) {
        searchTitle = $.t("topics.search.allTopicReviews");
      } else if (!assigneeSpecified && !creatorSpecified && !timeStampSpecified && type === 1) {
        searchTitle = $.t("topics.search.allUpdateResponses");
      } else {
        searchTitle = $.t("topics.search.taskSearchResults");
      }
      var returnObj = new TopicSearchModel.topicSearchResults({
        searchUrl: searchUrl,
        name: searchTitle,
      });
      return returnObj;
    },
    this,
  );

  Messenger.reqres.setHandler(
    "search:topics:by:topic:review:all:dates",
    function () {
      var kbId = Messenger.reqres.request("active:kb");
      var searchUrl = Properties.rootURL + "/" + kbId + "/tasks/topicReview/search";
      var searchTitle = $.t("topics.search.topicReviewAllResults");
      var returnObj = new TopicSearchModel.topicSearchResults({
        searchUrl: searchUrl,
        name: searchTitle,
      });
      return returnObj;
    },
    this,
  );

  Messenger.reqres.setHandler(
    "search:topics:by:context",
    function (contextId, contextName, topicType) {
      if (!topicType) topicType = TopicTypes.NON_ARCHIVED;
      var kbId = Messenger.reqres.request("active:kb");
      var searchUrl =
        Properties.rootURL +
        "/" +
        kbId +
        "/intents/groups/0/context/" +
        contextId +
        "/topicType/" +
        topicType;
      //create a topicSearchResults model and assign it a search url
      var searchTitle = $.t("topics.search.searchResults", { criteria: contextName });
      var returnObj = new TopicSearchModel.topicSearchResults({
        searchUrl: searchUrl,
        name: searchTitle,
      });
      return returnObj;
    },
    this,
  );

  Messenger.reqres.setHandler(
    "search:topics:by:scheduled:status",
    function (statusId, timestamp, statusName, topicType) {
      if (!topicType) topicType = TopicTypes.NON_ARCHIVED;
      var kbId = Messenger.reqres.request("active:kb");
      var searchUrl =
        Properties.rootURL +
        "/" +
        kbId +
        "/intents/groups/0/status/" +
        statusId +
        "/scheduled/" +
        timestamp +
        "/topicType/" +
        topicType;
      //create a topicSearchResults model and assign it a search url
      var searchTitle = $.t("topics.search.searchResults", { criteria: statusName });
      var returnObj = new TopicSearchModel.topicSearchResults({
        searchUrl: searchUrl,
        name: searchTitle,
      });
      return returnObj;
    },
    this,
  );

  Messenger.reqres.setHandler(
    "search:topics:by:category",
    function (catId, catValueId, catName, catValueName, topicType) {
      if (!topicType) topicType = TopicTypes.NON_ARCHIVED;
      var kbId = Messenger.reqres.request("active:kb");
      var searchUrl =
        Properties.rootURL +
        "/" +
        kbId +
        "/intents/groups/0/category/" +
        catId +
        "/value/" +
        catValueId +
        "/topicType/" +
        topicType;
      //create a topicSearchResults model and assign it a search url
      var searchTitle = $.t("topics.search.searchResults", {
        criteria: catName + "/" + catValueName,
      });
      var returnObj = new TopicSearchModel.topicSearchResults({
        searchUrl: searchUrl,
        name: searchTitle,
      });
      return returnObj;
    },
    this,
  );

  Messenger.reqres.setHandler(
    "search:topics:by:categories",
    function (catIds, catValueIds, catName, catValueName, numCategories, topicType) {
      if (!topicType) topicType = TopicTypes.NON_ARCHIVED;
      var kbId = Messenger.reqres.request("active:kb");
      var searchUrl =
        Properties.rootURL +
        "/" +
        kbId +
        "/intents/groups/0/categories/" +
        catIds +
        "/values/" +
        catValueIds +
        "/topicType/" +
        topicType;
      //create a topicSearchResults model and assign it a search url
      var searchTitle = $.t("topics.search.searchResultsMultiple", {
        criteria: catName + "/" + catValueName,
        size: numCategories - 1,
      });
      var returnObj = new TopicSearchModel.topicSearchResults({
        searchUrl: searchUrl,
        name: searchTitle,
      });
      return returnObj;
    },
    this,
  );

  Messenger.reqres.setHandler(
    "search:and:replace:responses",
    function (findValue, replaceValue, touchpoint, language, matchCase, wholeWord, topicType) {
      if (!topicType) topicType = TopicTypes.NON_ARCHIVED;
      //create a topicSearchResults model and assign it a search url
      var searchTitle = $.t("topics.search.replaceResults", {
        find: findValue,
        replace: replaceValue,
      });
      var returnObj = new TopicSearchModel.topicReplaceResults({
        name: searchTitle,
      });
      returnObj.findAndReplace(
        findValue,
        replaceValue,
        touchpoint,
        language,
        matchCase,
        wholeWord,
        topicType,
      );
      return returnObj;
    },
    this,
  );

  Messenger.reqres.setHandler(
    "get:topicTypes",
    function () {
      return TopicTypes;
    },
    this,
  );

  Messenger.reqres.setHandler(
    "navigationButtons:archived",
    function () {
      return { showButtonsForArchived: true, showArchivedTopics: true, showButtons: true };
    },
    this,
  );

  Messenger.reqres.setHandler(
    "navigationButtons:search:all",
    function () {
      return { showButtonsForArchived: false, showArchivedTopics: false, showButtons: false };
    },
    this,
  );
  Messenger.reqres.setHandler(
    "navigationButtons:search:archived",
    function () {
      return { showButtonsForArchived: true, showArchivedTopics: false, showButtons: true };
    },
    this,
  );
  Messenger.reqres.setHandler(
    "navigationButtons:search",
    function () {
      return { showButtonsForArchived: false, showArchivedTopics: false, showButtons: true };
    },
    this,
  );

  Messenger.reqres.setHandler(
    "search:topics:by:attributes",
    function (attributeName, topicType) {
      if (!topicType) topicType = TopicTypes.NON_ARCHIVED;
      var kbId = Messenger.reqres.request("active:kb");
      var searchUrl =
        `${Properties.rootURL}/${kbId}/` +
        `intents/topicType/${topicType}/attributes/search` +
        `?attributeName=${encodeURIComponent(attributeName)}`;
      var searchTitle = $.t("topics.search.searchResults", {
        criteria: attributeName.escapeHtml(),
      });
      var returnObj = new TopicSearchModel.topicSearchResults({
        searchUrl: searchUrl,
        name: searchTitle,
      });
      return returnObj;
    },
    this,
  );
});
